import { scoreBg, scoreColor } from '@workspace/seedforge-shared'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowDownRight, ArrowUpRight, FileText } from 'lucide-react'
import { VideoThumbnail } from '../../components/video-thumbnail.js'
import type { EvalMetric } from '../types'

interface MetricScoresPanelProps {
  metrics: EvalMetric[]
  hideMedia?: boolean
  /** Compact mode: hides readiness badge and delta indicators. Used for investor view. */
  compact?: boolean
  /** Pitch duration label (e.g. "32:15") shown on video thumbnail */
  pitchDuration?: string
  /** Video URL for inline preview */
  videoUrl?: string | null
  /** Whether the video modal is currently open */
  videoModalOpen?: boolean
  /** Time to resume preview at after modal closes */
  videoResumeTime?: number
  /** Called when user clicks the video thumbnail — passes current preview time */
  onPlayVideo?: (currentTime: number) => void
  /** Called when user clicks the Pitch Deck button */
  onOpenPitchDeck?: () => void
}

export { MetricScoresPanel as EvaluationMetricScores }

export function MetricScoresPanel({
  metrics,
  hideMedia,
  compact,
  pitchDuration,
  videoUrl,
  videoModalOpen,
  videoResumeTime,
  onPlayVideo,
  onOpenPitchDeck,
}: MetricScoresPanelProps) {
  if (metrics.length === 0) return null

  return (
    <div className={cn('grid gap-6', hideMedia ? 'grid-cols-1' : 'grid-cols-3')}>
      <div
        className={cn(
          'flex flex-col justify-center rounded-xl border border-border bg-card/30 p-6',
          !hideMedia && 'col-span-2'
        )}
      >
        <div className="space-y-3">
          {metrics.map((m) => (
            <div key={m.key} className="flex items-center gap-4">
              <span className="w-32 shrink-0 text-right text-xs text-muted-foreground">
                {m.label}
              </span>
              <div className="relative h-2 flex-1 rounded-full bg-input">
                <div
                  className={cn('h-2 rounded-full', scoreBg(m.score))}
                  style={{ width: `${m.score}%` }}
                />
              </div>
              <span className={cn('w-8 text-right text-sm font-semibold', scoreColor(m.score))}>
                {m.score}
              </span>
              {!compact &&
                (m.delta && m.delta > 0 ? (
                  <div className="flex w-16 items-center gap-1 text-emerald-400">
                    <ArrowUpRight className="size-3" />
                    <span className="text-[10px] font-semibold">+{m.delta}</span>
                  </div>
                ) : m.delta && m.delta < 0 ? (
                  <div className="flex w-16 items-center gap-1 text-red-400/70">
                    <ArrowDownRight className="size-3" />
                    <span className="text-[10px] font-semibold">{m.delta}</span>
                  </div>
                ) : (
                  <div className="flex w-16 items-center gap-1 text-muted-foreground/80">
                    <span className="text-[10px]">—</span>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {!hideMedia && (
        <div className="flex flex-col gap-3">
          <VideoThumbnail
            videoUrl={videoUrl}
            duration={pitchDuration}
            modalOpen={videoModalOpen}
            resumeTime={videoResumeTime}
            onPlay={onPlayVideo}
            className="aspect-video rounded-xl border border-border transition-colors hover:border-[#f97316]/30"
          />
          <div className="flex gap-2">
            <SecondaryButton
              size="sm"
              fullWidth
              icon={<FileText />}
              onClick={onOpenPitchDeck}
              disabled={!onOpenPitchDeck}
            >
              Pitch deck
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  )
}
